import React, { useCallback } from 'react'

import { LinkItem } from '@frame/ui-components/unified-search/link-item'
import { NoResults } from '@frame/ui-components/unified-search/no-results'
import { WaitingForSearch } from '@frame/ui-components/unified-search/waiting-for-search'

import { InternalEventType } from '@frame/lib/api/events/types'
import { buildSearchResultViewProps } from '@frame/lib/api/events/utils'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { Item, ItemKind } from '@frame/lib/hooks/useGetItems'
import { cn } from '@frame/lib/utils/cn'
import { DataAttribute } from '@frame/types'

export type ResultsProps = {
  query?: string
  items: Item[]
  kind: ItemKind
  eventType?: InternalEventType
  handleSelectItem: ({ item }: { item: Item }) => void
}

export function Results({
  query,
  items,
  kind,
  eventType = InternalEventType.CLICKED_SEARCH_RESULT,
  handleSelectItem,
}: ResultsProps) {
  const { handleEvent } = useEventHandler()

  const handleSelect = useCallback(
    (item: Item, idx: number) => {
      handleEvent(
        eventType,
        buildSearchResultViewProps({
          index: idx,
          item_id: item?.id,
          item_kind: kind,
          item_name: item?.name,
          matching_score: item?.matching_score,
          total_results: items.length,
          query,
          other_items: items.map((i) => ({ item_name: i.name, item_id: i.id })),
        })
      )
      handleSelectItem({ item })
    },
    [handleEvent, eventType, kind, items, query, handleSelectItem]
  )

  if (items.length > 0) {
    return (
      <div data-cy={DataAttribute.UNIFIED_SEARCH_RESULTS}>
        {items.map((item, idx) => {
          return (
            <LinkItem
              key={item.id}
              showImage={eventType !== InternalEventType.BANK_INCOME_IDENTIFIED}
              item={item}
              handleSelect={() => handleSelect(item, idx)}
              className={cn('border-moss-300 py-4', idx < items.length - 1 && 'border-b-[1px]')}
            />
          )
        })}
      </div>
    )
  }

  if (!query && eventType === InternalEventType.CLICKED_SEARCH_RESULT) {
    return <WaitingForSearch kind={kind} />
  }

  return (
    <>
      <NoResults kind={kind} />
    </>
  )
}
